import { useEffect } from 'react'
import { Avatar, Box, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import AvatarIcon from '@mui/icons-material/PersonOutlined'
import SchoolIcon from '@mui/icons-material/SchoolOutlined'
import ClassIcon from '@mui/icons-material/ClassOutlined'
import GroupsIcon from '@mui/icons-material/GroupsOutlined'
import { useTranslation } from 'react-i18next'
import { MemberProvider } from 'components/Providers'
import { getGradeLevels } from 'store/lookup'
import NoRecords from 'components/NoRecords'
import 'utils/global.scss'

const EMPTY = '—'

function Field({ Icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Icon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || EMPTY}
        </Typography>
      </Box>
    </Stack>
  )
}

export default function Detail() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const studentList = useSelector(state => state.academic.parentStudent?.list)
  const student = useSelector(state => state.academic.parentStudent?.selectedStudent)
  const gradeLevels = useSelector(state => state.lookup?.gradeLevels)

  useEffect(() => {
    gradeLevels || dispatch(getGradeLevels())
  }, [dispatch, Boolean(gradeLevels)])

  const access = student?.personAccessViews?.[0]
  const member = studentList?.find(item => item?.personAccessViews?.[0]?.id === access?.id)

  const section = access?.academicYearEnrollment?.section
  const gradeLevelId = section?.gradeLevelId
  const gradeLevelName = gradeLevels?.find(level => String(level.id) === String(gradeLevelId))?.name

  const initials = [student?.firstName?.[0], student?.lastName?.[0]].filter(Boolean).join('').toUpperCase()
  const fullName = [student?.firstName, student?.lastName].filter(Boolean).join(' ')

  if (studentList === null) return <LinearProgress />

  if (!studentList?.length || !student) {
    return (
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <NoRecords
          message={t('No students linked to your account')}
          description={t('Please contact the school administration for assistance.')}
        />
      </Paper>
    )
  }

  return (
    <MemberProvider member={member}>
      <Paper id="student-detail" variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
        {studentList.length > 1 && (
          <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              {`${t('Viewing')} ${student?.firstName ?? ''} · ${t('switch students from the menu on the left')}`}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} alignItems="center" sx={{ p: 2.5 }}>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{ width: 64, height: 64, fontSize: 22, flexShrink: 0 }}
                src={student?.profilePicture?.location}
              >
                {initials || <AvatarIcon />}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {access?.campus?.name || t('No campus on file')}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Field Icon={ClassIcon} label={t('Grade Level')} value={gradeLevelName || gradeLevelId} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Field Icon={GroupsIcon} label={t('Section')} value={section?.gradeSection} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Field Icon={SchoolIcon} label={t('Campus')} value={access?.campus?.name} />
          </Grid>
        </Grid>

      </Paper>
    </MemberProvider>
  )
}
